import React, { useEffect, useState } from "react";
import { BiArrowBack } from "react-icons/bi";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { base_url } from "../utils/axiosConfig";
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { getUserCart, clearCart } from "../features/user/userSlice";
import { toast } from "react-toastify";
import { setAuthenticated, setUser } from "../utils/authAction";
import { LuLogIn } from "react-icons/lu";
import { Modal, Button } from "react-bootstrap";
import { SlActionRedo } from "react-icons/sl";
import { TbCash } from "react-icons/tb";



const validatePincode = async (pincode) => {
  if (!pincode) return false;
  try {
    const response = await axios.get(`${base_url}master/getpin`);
    const pincodedata = response.data;
    return pincodedata.some((item) => item.name.includes(pincode.toString()));
  } catch (error) {
    console.error("Pincode API Error:", error);
    return false;
  }
};

const shippingSchema = yup.object({
  firstName: yup.string().required("First Name is required *"),
  lastName: yup.string().required("Last Name is required *"),
  address: yup.string().required("Address is required *"),
  landmark: yup.string().required("Landmark is required *"),
  city: yup.string().required("City is required *"),
  state: yup.string().required("State is required *"),
  pincode: yup
    .string()
    .required("Pincode is required *")
    .test("is-valid-pincode", "This Pincode is not available", async (value) => await validatePincode(value)),
  phoneNumber: yup
    .string()
    .required("Phone number is required *")
    .matches(/^[0-9]{10}$/, "Phone number must be 10 digits"),
});

const Checkout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cartProducts, isAuthenticated, user } = useSelector((state) => state.auth);
  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [cashOndelivery, setCashOnDelivery] = useState(true);
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [useExistingAddress, setUseExistingAddress] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [pincodeError, setPincodeError] = useState(null);
  const [showCartModal, setShowCartModal] = useState(false);

  useEffect(() => {
  const fetchAddresses = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("customer"))?.token || localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get(`${base_url}user/getshippingadress`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      const addresses = response.data.shippingAddresses || [];
      setSavedAddresses(addresses);

      if (addresses.length > 0) {
        setSelectedAddressId(addresses[0]._id);
        setUseExistingAddress(true);
      } else if (user?.address1) {
        // Use address1 if no saved shipping addresses
        formik.setValues({
          ...formik.values,
          address: user.address1,
          firstName: user.firstname || "",
          lastName: user.lastname || "",
          city: "",
          state: "",
          pincode: user.pincode || "",
          landmark: "",
        });
        setUseExistingAddress(false);
      }
    } catch (error) {
      console.error("Error fetching addresses:", error.response?.data || error.message);
      toast.error("Failed to fetch saved addresses.");
    }
  };

  if (isAuthenticated) fetchAddresses();
}, [isAuthenticated, user]);


  useEffect(() => {
    const token = localStorage.getItem("token") || JSON.parse(localStorage.getItem("customer"))?.token;
    if (token) {
      dispatch(setAuthenticated(true));
      dispatch(setUser({ token }));
    }
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) dispatch(getUserCart());
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    const storedTotal = localStorage.getItem("checkoutTotal");
    if (storedTotal) {
      setTotalAmount(parseFloat(storedTotal).toFixed(2));
    } else if (cartProducts?.length > 0) {
      const sum = calculateSubTotal();
      setTotalAmount(sum);
    } else {
      const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
      setCartItems(savedCart);
      const sum = calculateSubTotal(savedCart);
      setTotalAmount(sum);
    }
  }, [cartProducts, isAuthenticated]);

  useEffect(() => {
    if (cartProducts?.length > 0) {
      setCartItems(cartProducts);
    } else {
      const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
      setCartItems(savedCart);
    }
  }, [cartProducts, isAuthenticated]);

  const calculateSubTotal = (items = cartItems) => {
    return items
      .reduce((acc, item) => {
        const price = item?.price || item?.ProductId?.offerPrice || item?.ProductId?.CurrentPrice || 0;
        const gstPercent = parseFloat(item?.ProductId?.gst) || 0;
        const gstAmount = (price * item.quantity * gstPercent) / 100;
        return acc + price * item.quantity + gstAmount;
      }, 0)
      .toFixed(2);
  };

  const onSubmit = async (values) => {
  if (!useExistingAddress) {
    const validationErrors = await shippingSchema.validate(values).catch((err) => err);
    if (validationErrors?.errors?.length) {
      if (validationErrors?.message?.includes("Pincode is not available")) {
        toast.error("Please enter a valid pincode.");
      } else {
        toast.error("Please fill all required shipping details correctly.");
      }
      return;
    }
  }

  if (!isAuthenticated) {
    toast.error("Please log in to proceed with your order.");
    setLoading(false);
    return;
  }
  if (useExistingAddress && !selectedAddressId) {
    toast.error("Please select a saved delivery address.");
    setLoading(false);
    return;
  }
  setLoading(true);

  const cartItemsToUse = cartItems.length > 0 ? cartItems : JSON.parse(localStorage.getItem("cart")) || [];
  if (cartItemsToUse.length === 0) {
    toast.error("Your cart is empty. Please add products.");
    setLoading(false);
    return;
  }

  const invalidItems = cartItemsToUse.filter(
    (item) => !item?.ProductId?._id || !item?.quantity || !item?.price
  );
  if (invalidItems.length > 0) {
    toast.error("Some cart items are invalid. Please review your cart.");
    setLoading(false);
    return;
  }

  const token = JSON.parse(localStorage.getItem("customer"))?.token || localStorage.getItem("token");
  if (!token) {
    toast.error("Your session has expired. Please log in again.");
    setLoading(false);
    return;
  }

let shippingInfo = useExistingAddress
  ? savedAddresses.find((addr) => addr._id === selectedAddressId)
  : values;

if (useExistingAddress && shippingInfo) {
  shippingInfo = {
    ...shippingInfo,
    firstName: shippingInfo.firstName || user?.firstName || "N/A",
    lastName: shippingInfo.lastName || user?.lastName || "N/A",
    address: shippingInfo.address || "",
    landmark: shippingInfo.landmark || "",
    city: shippingInfo.city || "",
    state: shippingInfo.state || "",
    pincode: shippingInfo.pincode || "",
    phoneNumber: values.phoneNumber || user?.phoneNumber || "",
    deliveryInstruction: values.deliveryInstruction || "",
  };
  }

  const requiredFields = ["address", "landmark", "city", "state", "pincode", "phoneNumber"];
  for (const field of requiredFields) {
    if (!shippingInfo[field] || shippingInfo[field] === "N/A") {
      toast.error(`Please fill in your ${field}.`);
      setLoading(false);
      return;
    }
  }

  const orderData = {
    shippingInfo,
    paymentInfo: { cashOndelivery },
    orderItems: cartItemsToUse.map((item) => ({
      product: item.ProductId._id,
      quantity: item.quantity,
      price: item.price,
    })),
    totalPrice: parseFloat(totalAmount),
    useExistingAddress,
    addressId: useExistingAddress ? selectedAddressId : null,
    deliveryInstruction: shippingInfo.deliveryInstruction || "",
    phoneNumber: shippingInfo.phoneNumber || "",
  };

  try {
    const response = await axios.post(`${base_url}user/cart/create-order`, orderData, {
      headers: {
        Authorization: `Bearer ${token}`,
        accept: "application/json",
      },
    });

    if (response.data.success && response.data.order && response.data.order._id) {
      const orderId = response.data.order._id;
      setOrderId(orderId);
      toast.success("Order placed successfully! Invoice sent to registered mail.");
      dispatch(clearCart());
      localStorage.removeItem("cart");
      localStorage.removeItem("checkoutTotal");
      setCartItems([]);
      navigate("/product");
    } else {
      toast.error("Order creation failed.");
      setLoading(false);
    }
  } catch (error) {
    console.error("Order Creation Error:", error.response?.data || error.message);
    setLoading(false);
    toast.error(error.response?.data?.message || "Failed to place order.");
  }
};

// Inside formik initial values
const formik = useFormik({
  initialValues: {
    firstName: user?.firstname || "",
    lastName: user?.lastname || "",
    address: user?.address1 || "",
    landmark: "",
    city: "",
    state: "",
    pincode: user?.pincode || "",
    phoneNumber: user?.phoneNumber || "",   // ✅ NEW FIELD
    deliveryInstruction: "",
  },
  validationSchema: useExistingAddress ? null : shippingSchema,
  onSubmit,
  validateOnChange: !useExistingAddress,
  validateOnBlur: !useExistingAddress,
});


  const handlePincodeChange = async (e) => {
    const pincode = e.target.value;
    formik.handleChange(e);
    if (pincode && !useExistingAddress) {
      try {
        const isValid = await validatePincode(pincode);
        setPincodeError(isValid ? null : "We currently do not deliver to this pincode.");
      } catch (error) {
        setPincodeError("Error validating pincode");
      }
    }
  };

  const handleLoginRedirect = () => {
    const formValues = formik.values;
    localStorage.setItem("checkoutFormData", JSON.stringify(formValues));
    localStorage.setItem("checkoutRedirect", "/checkout");
    if (cartItems?.length > 0) {
      localStorage.setItem("tempCart", JSON.stringify(cartItems));
    }
    window.location.href = "/login";
  };

  useEffect(() => {
    if (isAuthenticated) {
      const savedForm = localStorage.getItem("checkoutFormData");
      const savedCart = localStorage.getItem("tempCart");

      if (savedForm) {
        try {
          formik.setValues(JSON.parse(savedForm));
        } catch (error) {
          console.error("Error parsing saved form data", error);
        }
      }

      if (!cartProducts.length && savedCart) {
        try {
          setCartItems(JSON.parse(savedCart));
        } catch (error) {
          console.error("Error parsing saved cart data", error);
        }
      }

      localStorage.removeItem("checkoutFormData");
      localStorage.removeItem("tempCart");
    }
  }, [isAuthenticated]);

  return (
    <>
      <style jsx>{`
        
      `}</style>

      <div className="checkout-container">
        <div className="checkout-wrapper">
          <div className="row g-4">
            {/* Left Section - Shipping Details */}
            <div className="col-lg-8">
              <div className="shipping-card">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2 className="section-title mb-0">Delivery Address</h2>
                  {!user ? (
                                        
                    <button
                      onClick={handleLoginRedirect}
                      className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2 rounded-rounded shadow-sm"
                    >
                      <span>Login</span>
                      <LuLogIn size={18} />
                    </button>
                  ) : (
                    <span className="welcome-badge">Welcome, {user.email}</span>
                  )}
                </div>

                {!isAuthenticated && (
                  <p className="text-danger mb-2">  Please login to continue with shipping <span><SlActionRedo  /></span></p>
                )}  

                {/* Saved Addresses */}
                {isAuthenticated && savedAddresses.length > 0 && (
                  <div className="mb-4">
                    <h3 className="subtitle">Select Saved Address</h3>
                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="useExistingAddress"
                        checked={useExistingAddress}
                        onChange={() => setUseExistingAddress(!useExistingAddress)}
                      />
                      <label className="form-check-label" htmlFor="useExistingAddress">
                        Use saved address
                      </label>
                    </div>
                    {useExistingAddress && (
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {savedAddresses.map((address) => (
                          <div key={address._id} className="address-item">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="savedAddress"
                                id={`address-${address._id}`}
                                value={address._id}
                                checked={selectedAddressId === address._id}
                                onChange={(e) => setSelectedAddressId(e.target.value)}
                              />
                              <label className="form-check-label w-100 p-2" htmlFor={`address-${address._id}`}>
                                <div className="small">
                                  {`${address.firstName || ''} ${address.lastName || ''}`}<br />
                                  {`${address.address}, ${address.landmark || ''}`}<br />
                                  {`${address.city}, ${address.state} - ${address.pincode}`}
                                </div>
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Shipping Form */}
                {(!isAuthenticated || savedAddresses.length === 0 || !useExistingAddress) && (
                  <form onSubmit={formik.handleSubmit}>
                    <div className="row g-1">
                      {[
                        { name: "firstName", placeholder: "First Name", col: "col-md-6" },
                        { name: "lastName", placeholder: "Last Name", col: "col-md-6" },
                        { name: "landmark", placeholder: "Landmark", col: "col-md-6" },
                        { name: "city", placeholder: "City", col: "col-md-6" },
                        { name: "state", placeholder: "State", col: "col-md-6" },
                        { name: "pincode", placeholder: "Pincode", onChange: handlePincodeChange, col: "col-md-6" },
                      ].map(({ name, placeholder, onChange, col }) => (
                        <div className={col} key={name}>
                          <input
                            type="text"
                            name={name}
                            className="form-input"
                            placeholder={placeholder}
                            onChange={onChange || formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values[name]}
                            disabled={!isAuthenticated}
                          />
                          {formik.touched[name] && formik.errors[name] && (
                            <div className="error-text">{formik.errors[name]}</div>
                          )}
                          {name === "pincode" && pincodeError && <div className="error-text">{pincodeError}</div>}
                        </div>
                        
                      ))}
                      <div className="col-md-6">
                        <input
                          type="text"
                          name="phoneNumber"
                          className="form-input"
                          placeholder="Phone Number"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.phoneNumber}
                          disabled={!isAuthenticated}
                        />
                        {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                          <div className="error-text">{formik.errors.phoneNumber}</div>
                        )}
                      </div>
                      <div className="col-12">
                        <input
                          type="text"
                          name="address"
                          className="form-input"
                          placeholder="Full Address"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.address}
                          disabled={!isAuthenticated}
                        />
                        {formik.touched.address && formik.errors.address && (
                          <div className="error-text">{formik.errors.address}</div>
                        )}
                      </div>
                      <div className="col-12">
                        <textarea
                          name="deliveryInstruction"
                          className="form-input"
                          placeholder="Delivery Instructions (optional)"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.deliveryInstruction}
                          rows="3"
                          disabled={!isAuthenticated}
                        />
                        {formik.touched.deliveryInstruction && formik.errors.deliveryInstruction && (
                          <div className="error-text">{formik.errors.deliveryInstruction}</div>
                        )}
                      </div>

                    </div>
                  </form>
                )}
                
              </div>
            </div>

            {/* Right Section - Order Summary */}
            <div className="col-lg-4">
              <div className="order-summary-card">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2 className="section-title mb-0">Order Summary</h2>
                  <Link to="/Cart" className="text-primary text-decoration-none d-flex align-items-center small">
                    <BiArrowBack className="me-1" /> Back to Cart
                  </Link>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-4">
                  <p className="mb-0 text-muted">
                    Total Items: <strong>{cartItems.reduce((acc, item) => acc + (item.quantity || 0), 0)}</strong>
                  </p>
                  <button className="btn btn-primary p-0 text-light py-1 px-3" onClick={() => setShowCartModal(true)}>
                    View Details
                  </button>
                </div>

                <div className="payment-option mb-4">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      id="cashOndelivery"
                      checked={cashOndelivery}
                      onChange={() => setCashOnDelivery(!cashOndelivery)}
                      disabled={!isAuthenticated}
                    />
                    <label className="   mb-0 w-100" htmlFor="cashOndelivery">
                      <div className="d-flex align-items-center">
                        <span className="me-2"><TbCash /></span>
                        <span>Cash on Delivery</span>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="text-center border-top pt-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="mb-0 total-amount">₹{totalAmount}</h3>
                  </div>
                  <button
                    className="btn btn-primary-custom w-100 d-flex align-items-center justify-content-center"
                    disabled={!isAuthenticated || loading || (useExistingAddress && !selectedAddressId)}
                    onClick={async () => {
                      if (!useExistingAddress) {
                        const errors = await formik.validateForm();
                        if (errors?.pincode) {
                          toast.error("Please enter a valid pincode.");
                          return;
                        }
                      }
                      formik.handleSubmit();
                    }}
                  >
                    {loading ? (
                      <>
                        <div className="spinner-custom"></div>
                        Processing...
                      </>
                    ) : (
                      "Place Order"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
          </div>
        )}

        {/* Cart Modal */}
        <Modal show={showCartModal} onHide={() => setShowCartModal(false)} centered size="md">
          <Modal.Header closeButton className="border-bottom">
            <Modal.Title className="h5 mb-0">Your Cart Items</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {cartItems.length > 0 ? (
              cartItems.map((item, i) => (
                <div key={i} className="modal-cart-item">
                  <img
                    src={item?.ProductId?.images?.[0]?.url ? `${base_url}${item.ProductId.images[0].url}` : "/default.jpg"}
                    className="cart-item-img"
                    alt={item?.ProductId?.title}
                  />
                  <div className="item-details">
                    <div className="item-title">{item?.ProductId?.title}</div>
                    <div className="small text-muted">Qty: {item?.quantity}</div>
                    <div className="item-price">₹{item?.price}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-danger py-4">
                <p className="mb-0">Your cart is empty.</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="border-top">
            <Button variant="secondary" onClick={() => setShowCartModal(false)}>
              Close
            </Button>
            <Link to="/Cart" className="btn btn-primary">
              Go to Cart
            </Link>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default Checkout;